<!-- Notifications toggle -->
<div class="flex flex-row gap-2">
  @if (!(isNewNavigationOn$ | async)) {
    <ts-button
      htmlButtonType="button"
      type="icon"
      class="notifications-toggle"
      (buttonClick)="goToFolders()"
      svgIcon="folder"
    ></ts-button>
    <ts-button
      htmlButtonType="button"
      type="icon"
      class="notifications-toggle"
      (buttonClick)="goToCalendar()"
      svgIcon="calendar_today"
    ></ts-button>
  }
  <div class="relative">
    @if (totalBadgeCount() > 0) {
      <div class="notification-count-badge">
        {{ totalBadgeCount() > 99 ? '99+' : totalBadgeCount() }}
      </div>
    }
    @if (showNewBadge()) {
      <div class="notification-new-badge">NEW</div>
    }

    @if (isNewNavigationOn$ | async) {
      <ts-button
        type="icon"
        icon="bell"
        class="cursor-pointer"
        (buttonClick)="openPanel()"
        iconColor="black"
        lucideIcon="true"
        #notificationsOrigin
      ></ts-button>
    } @else {
      <ts-button
        htmlButtonType="button"
        type="icon"
        class="notifications-toggle"
        (buttonClick)="openPanel()"
        #notificationsOrigin
        svgIcon="notifications"
      ></ts-button>
    }
  </div>
</div>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
  <div class="notifications-panel">
    <!-- Main View -->
    @if (panelView() === 'main') {
      <div class="header">
        <div class="title">Notifications</div>
        <div class="header-actions">
          <ts-button
            htmlButtonType="button"
            type="icon"
            [buttonTooltip]="'Mark all as read'"
            (buttonClick)="markAllAsRead()"
            svgIcon="playlist_add_check"
          ></ts-button>
          <ts-button variant="secondary" class="archived-button" (buttonClick)="openArchived()">
            <mat-icon [svgIcon]="'ts_archive'"></mat-icon>
            <span>Archived</span>
          </ts-button>
        </div>
      </div>

      <div class="content">
        <!-- In Progress Section -->
        @if (inProgressJobs().length > 0) {
          <div class="section-header">
            <span>In Progress</span>
            <span class="section-badge">{{ inProgressCount() }}</span>
          </div>

          @for (job of inProgressJobs(); track job.id) {
            <div class="notification in-progress-item">
              <div class="item-content">
                <mat-spinner diameter="18" class="in-progress-spinner"></mat-spinner>
                <div class="notification-text">
                  <div class="notification-title">{{ getJobTitle(job) }}</div>
                  <div class="notification-subtitle">In progress</div>
                  <div class="notification-time">{{ getJobTime(job) | timeAgo }}</div>
                </div>
              </div>
            </div>
          }
        }

        <!-- Completed Section -->
        @if (completedExports().length > 0 || notifications().length > 0) {
          <div class="section-header">
            <span>Completed</span>
            <span class="section-badge">{{ completedCount() }}</span>
          </div>

          @for (job of completedExports(); track job.id) {
            <div class="notification completed-item">
              <div class="item-content">
                <div class="completed-check">
                  <mat-icon [svgIcon]="'check_circle'"></mat-icon>
                </div>
                <div class="notification-text">
                  <div class="notification-title">{{ getJobTitle(job) }}</div>
                  <div class="notification-subtitle">Export complete</div>
                  <div class="notification-time">{{ getJobTime(job) | timeAgo }}</div>
                </div>
              </div>
              <div class="hover-actions">
                @if (job.download_url || job.latest_document_url) {
                  <ts-button htmlButtonType="button" type="icon" (buttonClick)="downloadExport(job)">
                    <ts-icon icon="open_in_new"></ts-icon>
                  </ts-button>
                }
              </div>
            </div>
          }

          @for (notification of notifications(); track notification.id) {
            <div class="notification completed-item unread">
              <div class="item-content">
                <div
                  class="completed-check"
                  [ngClass]="{ 'completed-check--failed': isFailedNotification(notification) }"
                >
                  <mat-icon [svgIcon]="isFailedNotification(notification) ? 'cancel' : 'check_circle'"></mat-icon>
                </div>
                <div class="notification-text">
                  <div class="notification-title">
                    {{ notification.subject }}
                  </div>
                  <div class="notification-subtitle">{{ notification.body }}</div>
                  <div class="notification-time">{{ notification.created_at | timeAgo }}</div>
                </div>
              </div>
              <div class="hover-actions">
                @if (notification.link) {
                  <ts-button
                    htmlButtonType="button"
                    type="icon"
                    (buttonClick)="openLink(notification)"
                    [buttonTooltip]="'Open'"
                  >
                    <ts-icon icon="open_in_new"></ts-icon>
                  </ts-button>
                }
                <ts-button
                  htmlButtonType="button"
                  type="icon"
                  (buttonClick)="markAsRead(notification.id)"
                  [buttonTooltip]="'Dismiss'"
                  svgIcon="close"
                ></ts-button>
              </div>
            </div>
          }
        }

        @if (inProgressJobs().length === 0 && completedExports().length === 0 && notifications().length === 0) {
          <div class="empty">
            <mat-icon [svgIcon]="'notifications'"></mat-icon>
            <span>No notifications</span>
          </div>
        }
      </div>
    }

    <!-- Archived View -->
    @if (panelView() === 'archived') {
      <div class="header archived-header">
        <ts-button
          htmlButtonType="button"
          type="icon"
          (buttonClick)="closeArchived()"
          class="back-button"
          svgIcon="arrow_back"
        ></ts-button>
        <div class="title">Archived</div>
      </div>

      <div class="content">
        @if (archivedNotifications().length > 0) {
          @for (notification of archivedNotifications(); track notification.id) {
            <div class="notification archived-item">
              <div class="item-content">
                <div
                  class="completed-check archived"
                  [ngClass]="{ 'completed-check--failed': isFailedNotification(notification) }"
                >
                  <mat-icon [svgIcon]="isFailedNotification(notification) ? 'cancel' : 'check_circle'"></mat-icon>
                </div>
                <div class="notification-text">
                  <div class="notification-title">{{ notification.subject }}</div>
                  <div class="notification-subtitle">{{ notification.body }}</div>
                  <div class="notification-time">{{ notification.created_at | timeAgo }}</div>
                </div>
              </div>
            </div>
          }
        } @else {
          <div class="empty">
            <mat-icon [svgIcon]="'inventory_2'"></mat-icon>
            <span>No archived notifications</span>
          </div>
        }
      </div>
    }
  </div>
</ng-template>
